/* ─── Invoice Utilities ──────────────────────────────────────────────────────
 * Ported from v1 App.js — keep exact same logic.
 * All pure functions; no React imports.
 * ─────────────────────────────────────────────────────────────────────────── */

/* ─── Types ──────────────────────────────────────────────────────────────── */

export interface LineItem {
  description?: string;
  quantity?: number | string;
  unit_price?: number | string;
  price?: number | string;
  tax_percentage?: number | string;
  line_total?: number | string;
  line_total_usd?: number | string;
}

export interface InvoiceRecord {
  invoice_id?: number | string;
  invoice_number?: string;
  invoice_date?: string;
  due_date?: string;
  paid_date?: string;
  subject?: string;
  status?: string;
  currency?: string;
  entity_name?: string;
  amount?: number | string;
  amount_usd?: number | string;
  balance?: number | string;
  balance_usd?: number | string;
  paid_amount?: number | string;
  paid?: number | string;
  line_items?: LineItem[];
}

export interface ProjectAssociation {
  project_name?: string;
  name?: string;
  project_id?: number | string;
  entity_name?: string;
  billing_entity?: string;
  entity?: string;
  status?: string;
  [key: string]: unknown;
}

export type CurrencyView = 'USD' | 'BASE';

export interface InvoiceRowResult {
  inv: number;
  paid: number;
  out: number;
  cancelledInv: number;
  invInTotals: number;
  paidInTotals: number;
}

export interface InvoiceFinancialTotals {
  totalInvoicedUsd: number;
  totalPaidUsd: number;
  outstandingUsd: number;
  cancelledUsd: number;
  totalInvoicedBase: number;
  totalPaidBase: number;
  outstandingBase: number;
  cancelledBase: number;
}

export interface DepositMetrics {
  depositBase: number;
  depositAdjustmentBase: number;
  netDepositBase: number;
  depositUsd: number;
  depositAdjustmentUsd: number;
  netDepositUsd: number;
}

export interface DepositAnalysis {
  metrics: DepositMetrics;
  paidDepositInvoices: InvoiceRecord[];
  unpaidDepositSubjectInvoices: InvoiceRecord[];
  adjustmentRows: Array<{ invoice: InvoiceRecord; line: LineItem; lineIndex: number }>;
}

export interface PaymentDistribution {
  paidPct: number | null;
  outstandingPct: number | null;
  cancelledPct: number | null;
}

export interface InvoiceFilters {
  statusFilter?: string[];
  entityFilter?: string[];
  invoiceSearch?: string;
  /** Billed calendar years (OR). Empty = no calendar-year constraint from this axis. */
  calendarYears?: string[];
  /** Billed months '01'–'12' (OR). With years: invoice must match one year and one month. */
  calendarMonths?: string[];
  /** India FY start years e.g. '2024' (OR). */
  fyYears?: string[];
  /** Q1–Q4 (OR) with fyYears; empty = full FY for each selected start year. */
  fyQuarters?: string[];
}

export interface BuildDepositCsvParams {
  clientName?: string;
  contactId?: number | string;
  baseCurrency?: string;
  metrics: DepositMetrics;
  paidDepositInvoices: InvoiceRecord[];
  unpaidDepositSubjectInvoices: InvoiceRecord[];
  adjustmentRows: Array<{ invoice: InvoiceRecord; line: LineItem; lineIndex: number }>;
}

/* ─── Constants ──────────────────────────────────────────────────────────── */

const DEPOSIT_INVOICE_SUBJECT_MARKERS = ['deposit', 'advance', 'upfront'];
const DEPOSIT_ADJUSTMENT_KEYWORDS = ['deposit', 'deposit adjusted', 'adjusted', 'adjustment', 'adjusting'];
const LESS_ADVANCE_DEPOSIT_PATTERN = /less\s*:\s*advance/i;

/**
 * Preferred ordering for status filter labels that appear in invoice data.
 * Statuses not listed here sort alphabetically after known ones.
 */
export const INVOICE_STATUS_FILTER_PRESETS: string[] = [
  'paid',
  'sent',
  'overdue',
  'draft',
  'void',
  'cancelled',
  'canceled',
  'authorized',
  'submitted',
  'viewed',
  'deleted',
  'unpaid',
  'open',
  'partially paid',
  'partially_paid',
  'sent/outstanding',
  'sent / outstanding',
];

/**
 * Distinct non-empty statuses from records (case-insensitive dedupe; first spelling kept),
 * sorted with {@link INVOICE_STATUS_FILTER_PRESETS} order first, then A–Z.
 */
export function mergeInvoiceStatusChipOptions(seenInRecords: string[]): string[] {
  const map = new Map<string, string>();
  for (const raw of seenInRecords) {
    if (raw == null) continue;
    const s = String(raw).trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (!map.has(k)) map.set(k, s);
  }
  const orderKeys = INVOICE_STATUS_FILTER_PRESETS.map((p) => p.trim().toLowerCase());
  return [...map.entries()]
    .sort((a, b) => {
      const ia = orderKeys.indexOf(a[0]);
      const ib = orderKeys.indexOf(b[0]);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a[1].localeCompare(b[1], undefined, { sensitivity: 'base' });
    })
    .map(([, display]) => display);
}

/* ─── Status helpers ─────────────────────────────────────────────────────── */

/** True when invoice is cancelled / void. */
export function invoiceStatusIsCancelled(invoice: InvoiceRecord): boolean {
  const s = String(invoice?.status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  return s === 'cancelled' || s === 'canceled' || s === 'void' || s === 'voided';
}

/* ─── Core invoice row computation ──────────────────────────────────────── */

/**
 * Per-invoice: `inv` / `paid` = table columns (gross).
 * Cancelled rows: out = 0.
 * Totals use `invInTotals` / `paidInTotals` (excludes cancelled) and `cancelledInv`.
 */
export function invoiceRowInvoicedPaidOutstanding(
  invoice: InvoiceRecord,
  currencyView: CurrencyView
): InvoiceRowResult {
  let inv: number;
  let paid: number;
  let out: number;

  if (currencyView === 'USD') {
    const amtUsd = parseFloat(String(invoice.amount_usd)) || 0;
    const balUsd = parseFloat(String(invoice.balance_usd)) || 0;
    inv = amtUsd;
    paid = balUsd;
    out = Math.max(0, amtUsd - balUsd);
  } else {
    const amount =
      parseFloat(String(invoice.amount)) || parseFloat(String(invoice.amount_usd)) || 0;
    let paidAmount: number | null = null;
    if (invoice.balance != null) {
      paidAmount = parseFloat(String(invoice.balance));
    } else if (invoice.paid_amount != null) {
      paidAmount = parseFloat(String(invoice.paid_amount));
    } else if (invoice.paid != null) {
      paidAmount = parseFloat(String(invoice.paid));
    }
    if (paidAmount === null || !Number.isFinite(paidAmount)) {
      const amountUsd = parseFloat(String(invoice.amount_usd)) || 0;
      const balanceUsd = parseFloat(String(invoice.balance_usd)) || 0;
      if (amount > 0 && amountUsd > 0 && balanceUsd > 0) {
        paidAmount = balanceUsd / (amountUsd / amount);
      } else {
        paidAmount = invoice.status === 'paid' && amount > 0 ? amount : 0;
      }
    }
    const p = paidAmount || 0;
    inv = amount;
    paid = p;
    out = Math.max(0, amount - p);
  }

  if (invoiceStatusIsCancelled(invoice)) {
    return { inv, paid, out: 0, cancelledInv: inv, invInTotals: 0, paidInTotals: 0 };
  }
  return { inv, paid, out, cancelledInv: 0, invInTotals: inv, paidInTotals: paid };
}

/* ─── Project associations ───────────────────────────────────────────────── */

/** Normalize contact_profile.project_associations to an array. */
export function normalizeClientProjectAssociations(raw: unknown): ProjectAssociation[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw as ProjectAssociation[];
  if (typeof raw === 'object' && raw !== null) {
    const r = raw as Record<string, unknown>;
    if (r.project != null) {
      return Array.isArray(r.project)
        ? (r.project as ProjectAssociation[])
        : [r.project as ProjectAssociation];
    }
    if (Array.isArray(r.projects)) return r.projects as ProjectAssociation[];
    if (Array.isArray(r.items)) return r.items as ProjectAssociation[];
    if (Array.isArray(r.associations)) return r.associations as ProjectAssociation[];
    const vals = Object.values(r);
    if (
      vals.length > 0 &&
      vals.every(
        (v) =>
          v && typeof v === 'object' &&
          ('project_name' in (v as object) || 'name' in (v as object) || 'project_id' in (v as object))
      )
    ) {
      return vals as ProjectAssociation[];
    }
    if ('project_name' in r || 'name' in r || 'project_id' in r) return [r as ProjectAssociation];
  }
  return [];
}

/* ─── Project / entity matching ──────────────────────────────────────────── */

/** Match invoice.entity_name to project (name or explicit entity fields). */
export function projectMatchesInvoiceEntity(project: ProjectAssociation, entityNameRaw: unknown): boolean {
  const ent = String(entityNameRaw || '').trim();
  if (!ent) return false;
  const entLower = ent.toLowerCase();
  const candidates = [
    project.entity_name,
    project.billing_entity,
    project.entity,
    project.project_name,
    project.name,
  ]
    .filter((x) => x != null && String(x).trim() !== '')
    .map((x) => String(x).trim().toLowerCase());
  return candidates.some((c) => c === entLower);
}

export function aggregateInvoiceTotalsForProject(
  project: ProjectAssociation,
  invoiceRecords: InvoiceRecord[],
  currencyView: CurrencyView
): { inv: number; paid: number } {
  let inv = 0;
  let paid = 0;
  if (!Array.isArray(invoiceRecords)) return { inv, paid };
  for (const invoice of invoiceRecords) {
    if (!projectMatchesInvoiceEntity(project, invoice.entity_name)) continue;
    const r = invoiceRowInvoicedPaidOutstanding(invoice, currencyView);
    inv += r.invInTotals;
    paid += r.paidInTotals;
  }
  return { inv, paid };
}

/* ─── Financial totals ───────────────────────────────────────────────────── */

/** Invoice summary KPIs using the same per-row rules as Invoice Details. */
export function aggregateInvoiceFinancialTotalsForSummary(records: InvoiceRecord[]): InvoiceFinancialTotals {
  const empty: InvoiceFinancialTotals = {
    totalInvoicedUsd: 0,
    totalPaidUsd: 0,
    outstandingUsd: 0,
    cancelledUsd: 0,
    totalInvoicedBase: 0,
    totalPaidBase: 0,
    outstandingBase: 0,
    cancelledBase: 0,
  };
  if (!Array.isArray(records) || records.length === 0) return empty;
  let iu = 0, pu = 0, ou = 0, cu = 0;
  let ib = 0, pb = 0, ob = 0, cb = 0;
  for (const inv of records) {
    const u = invoiceRowInvoicedPaidOutstanding(inv, 'USD');
    const b = invoiceRowInvoicedPaidOutstanding(inv, 'BASE');
    iu += u.invInTotals;
    pu += u.paidInTotals;
    ou += u.out;
    cu += u.cancelledInv;
    ib += b.invInTotals;
    pb += b.paidInTotals;
    ob += b.out;
    cb += b.cancelledInv;
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return {
    totalInvoicedUsd: r2(iu),
    totalPaidUsd: r2(pu),
    outstandingUsd: r2(ou),
    cancelledUsd: r2(cu),
    totalInvoicedBase: r2(ib),
    totalPaidBase: r2(pb),
    outstandingBase: r2(ob),
    cancelledBase: r2(cb),
  };
}

export function collectionRatioPercentFromSummaryTotals(totalsUsd: {
  totalInvoicedUsd: number;
  totalPaidUsd: number;
}): number | null {
  const inv = totalsUsd.totalInvoicedUsd;
  if (!(inv > 0)) return null;
  const p = (totalsUsd.totalPaidUsd / inv) * 100;
  return Math.round(p * 10) / 10;
}

/** Paid / outstanding / cancelled % of total face (USD). */
export function computePaymentDistributionPercentagesFromRecords(
  invoiceRecords: InvoiceRecord[]
): PaymentDistribution {
  if (!Array.isArray(invoiceRecords) || invoiceRecords.length === 0) {
    return { paidPct: null, outstandingPct: null, cancelledPct: null };
  }
  let nonCancelledFace = 0;
  let totalCancelled = 0;
  let totalPaid = 0;
  let outstanding = 0;
  for (const inv of invoiceRecords) {
    const amt = parseFloat(String(inv.amount_usd)) || 0;
    const bal = parseFloat(String(inv.balance_usd)) || 0;
    const st = (inv.status || '').toLowerCase();
    if (st === 'cancelled') {
      totalCancelled += amt;
      continue;
    }
    nonCancelledFace += amt;
    totalPaid += bal;
    outstanding += Math.max(0, amt - bal);
  }
  const denominator = nonCancelledFace + totalCancelled;
  if (denominator <= 0) return { paidPct: null, outstandingPct: null, cancelledPct: null };
  const r2 = (x: number) => Math.round(x * 100) / 100;
  return {
    paidPct: r2((totalPaid / denominator) * 100),
    outstandingPct: r2((outstanding / denominator) * 100),
    cancelledPct: r2((totalCancelled / denominator) * 100),
  };
}

/* ─── Paid amount helpers ────────────────────────────────────────────────── */

/** Paid amount in invoice currency (base). */
export function invoicePaidAmountBase(inv: InvoiceRecord): number {
  let paidAmount: number = NaN;
  if (inv.balance != null && inv.balance !== '') {
    paidAmount = parseFloat(String(inv.balance));
  } else if (inv.paid_amount != null && inv.paid_amount !== '') {
    paidAmount = parseFloat(String(inv.paid_amount));
  } else if (inv.paid != null && inv.paid !== '') {
    paidAmount = parseFloat(String(inv.paid));
  }
  if (!Number.isFinite(paidAmount)) {
    const amount = parseFloat(String(inv.amount)) || parseFloat(String(inv.amount_usd)) || 0;
    const amountUsd = parseFloat(String(inv.amount_usd)) || 0;
    const balanceUsd = parseFloat(String(inv.balance_usd)) || 0;
    if (amount > 0 && amountUsd > 0 && balanceUsd > 0) {
      paidAmount = balanceUsd / (amountUsd / amount);
    } else if ((inv.status || '').toLowerCase() === 'paid' && amount > 0) {
      paidAmount = amount;
    } else {
      paidAmount = 0;
    }
  }
  return paidAmount || 0;
}

export function depositInvoiceAmountBase(inv: InvoiceRecord): number {
  const base = parseFloat(String(inv.amount));
  if (Number.isFinite(base)) return base;
  return parseFloat(String(inv.amount_usd)) || 0;
}

/* ─── Deposit predicates ─────────────────────────────────────────────────── */

export function subjectIndicatesDepositRecord(inv: InvoiceRecord): boolean {
  const s = (inv.subject || '').trim().toLowerCase();
  return DEPOSIT_INVOICE_SUBJECT_MARKERS.some((m) => s.includes(m));
}

export function isDepositInvoiceRecord(inv: InvoiceRecord): boolean {
  const status = (inv.status || '').trim();
  return subjectIndicatesDepositRecord(inv) && status === 'paid';
}

export function invoiceSubjectMentionsDeposit(inv: InvoiceRecord): boolean {
  return subjectIndicatesDepositRecord(inv);
}

export function descriptionMatchesDepositAdjustmentRecord(descRaw: unknown): boolean {
  const descLower = (String(descRaw || '')).trim().toLowerCase();
  if (DEPOSIT_ADJUSTMENT_KEYWORDS.some((kw) => descLower.includes(kw))) return true;
  return LESS_ADVANCE_DEPOSIT_PATTERN.test(String(descRaw || ''));
}

export function isDepositAdjustmentLineRecord(line: LineItem): boolean {
  if (!descriptionMatchesDepositAdjustmentRecord(line.description)) return false;
  const lineTotal = parseFloat(String(line.line_total));
  const lineTotalUsd = parseFloat(String(line.line_total_usd));
  return (
    (Number.isFinite(lineTotal) && lineTotal < 0) ||
    (Number.isFinite(lineTotalUsd) && lineTotalUsd < 0)
  );
}

/* ─── Deposit line amounts ───────────────────────────────────────────────── */

export function depositAdjustmentLineAmountBase(line: LineItem): number {
  const lineTotal = parseFloat(String(line.line_total));
  if (Number.isFinite(lineTotal) && lineTotal < 0) return Math.abs(lineTotal);
  const lineTotalUsd = parseFloat(String(line.line_total_usd));
  if (Number.isFinite(lineTotalUsd) && lineTotalUsd < 0) return Math.abs(lineTotalUsd);
  return 0;
}

export function depositAdjustmentLineAmountUsd(line: LineItem): number {
  const lineTotalUsd = parseFloat(String(line.line_total_usd));
  if (Number.isFinite(lineTotalUsd) && lineTotalUsd < 0) return Math.abs(lineTotalUsd);
  const lineTotal = parseFloat(String(line.line_total));
  if (Number.isFinite(lineTotal) && lineTotal < 0 && Number.isFinite(lineTotalUsd))
    return Math.abs(lineTotalUsd);
  return 0;
}

/* ─── Deposit metrics ────────────────────────────────────────────────────── */

export function computeDepositMetricsFromRecords(invoiceRecords: InvoiceRecord[]): DepositMetrics {
  const empty: DepositMetrics = {
    depositBase: 0,
    depositAdjustmentBase: 0,
    netDepositBase: 0,
    depositUsd: 0,
    depositAdjustmentUsd: 0,
    netDepositUsd: 0,
  };
  if (!Array.isArray(invoiceRecords)) return empty;

  let depositUsd = 0;
  let depositAdjUsd = 0;
  let depositBase = 0;
  let depositAdjBase = 0;
  for (const inv of invoiceRecords) {
    if (isDepositInvoiceRecord(inv)) {
      depositUsd += parseFloat(String(inv.amount_usd)) || 0;
      depositBase += depositInvoiceAmountBase(inv);
    }
    for (const line of inv.line_items || []) {
      if (isDepositAdjustmentLineRecord(line)) {
        depositAdjUsd += depositAdjustmentLineAmountUsd(line);
        depositAdjBase += depositAdjustmentLineAmountBase(line);
      }
    }
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  depositBase = r2(depositBase);
  depositAdjBase = r2(depositAdjBase);
  depositUsd = r2(depositUsd);
  depositAdjUsd = r2(depositAdjUsd);
  return {
    depositBase,
    depositAdjustmentBase: depositAdjBase,
    netDepositBase: r2(depositBase - depositAdjBase),
    depositUsd,
    depositAdjustmentUsd: depositAdjUsd,
    netDepositUsd: r2(depositUsd - depositAdjUsd),
  };
}

export function analyzeDepositDetails(invoiceRecords: InvoiceRecord[]): DepositAnalysis {
  const metrics = computeDepositMetricsFromRecords(invoiceRecords);
  if (!Array.isArray(invoiceRecords)) {
    return { metrics, paidDepositInvoices: [], unpaidDepositSubjectInvoices: [], adjustmentRows: [] };
  }
  const paidDepositInvoices = invoiceRecords.filter(isDepositInvoiceRecord);
  const unpaidDepositSubjectInvoices = invoiceRecords.filter(
    (inv) => invoiceSubjectMentionsDeposit(inv) && !isDepositInvoiceRecord(inv)
  );
  const adjustmentRows: DepositAnalysis['adjustmentRows'] = [];
  invoiceRecords.forEach((inv) => {
    (inv.line_items || []).forEach((line, lineIndex) => {
      if (isDepositAdjustmentLineRecord(line)) {
        adjustmentRows.push({ invoice: inv, line, lineIndex });
      }
    });
  });
  return { metrics, paidDepositInvoices, unpaidDepositSubjectInvoices, adjustmentRows };
}

/* ─── Filtering ──────────────────────────────────────────────────────────── */

export function getBilledYearMonth(invoiceSentDateOrYm: unknown): string | null {
  if (!invoiceSentDateOrYm || typeof invoiceSentDateOrYm !== 'string') return null;
  const s = String(invoiceSentDateOrYm).trim().substring(0, 7);
  if (s.length < 7) return null;
  const parts = s.split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || m < 1 || m > 12) return null;
  let billedMonth = m - 1;
  let billedYear = y;
  if (billedMonth < 1) {
    billedMonth = 12;
    billedYear = y - 1;
  }
  return `${billedYear}-${String(billedMonth).padStart(2, '0')}`;
}

export function invoiceInCalendarPeriodForFilter(
  inv: InvoiceRecord,
  y: string,
  m: string | null
): boolean {
  if (!y) return true;
  const billedYm = getBilledYearMonth(inv.invoice_date);
  if (!billedYm) return false;
  const [invY, invM] = billedYm.split('-');
  if (m) return String(invY) === String(y) && invM === m;
  return String(invY) === String(y);
}

export function invoiceInFYPeriodForFilter(
  inv: InvoiceRecord,
  fyY: string,
  q: string | null
): boolean {
  if (!fyY) return true;
  const billedYm = getBilledYearMonth(inv.invoice_date);
  if (!billedYm) return false;
  const y = parseInt(billedYm.split('-')[0], 10);
  const mo = parseInt(billedYm.split('-')[1], 10);
  const startFY = parseInt(fyY, 10);
  if (!q) {
    if (mo >= 4) return y === startFY;
    return y === startFY + 1;
  }
  if (q === 'Q1') return y === startFY && mo >= 4 && mo <= 6;
  if (q === 'Q2') return y === startFY && mo >= 7 && mo <= 9;
  if (q === 'Q3') return y === startFY && mo >= 10 && mo <= 12;
  if (q === 'Q4') return y === startFY + 1 && mo >= 1 && mo <= 3;
  return false;
}

export function invoiceInAnyFYPeriodForFilter(
  inv: InvoiceRecord,
  fyStartYears: string[],
  q: string | null
): boolean {
  if (!fyStartYears || fyStartYears.length === 0) return true;
  for (let i = 0; i < fyStartYears.length; i++) {
    if (invoiceInFYPeriodForFilter(inv, String(fyStartYears[i]), q)) return true;
  }
  return false;
}

function normalizeBilledMonthToken(m: string): string {
  const n = parseInt(String(m).trim(), 10);
  if (Number.isNaN(n) || n < 1 || n > 12) return '';
  return String(n).padStart(2, '0');
}

/** Billed calendar: years OR-months semantics — year list AND month list (each OR within). */
export function invoiceInAnyCalendarSelection(
  inv: InvoiceRecord,
  years: string[],
  months: string[],
): boolean {
  const billedYm = getBilledYearMonth(inv.invoice_date);
  if (!billedYm) return false;
  const [invY, invM] = billedYm.split('-');
  const yOk = years.length === 0 || years.some((y) => String(invY) === String(y));
  const monthPads = months.map(normalizeBilledMonthToken).filter(Boolean);
  const mOk = monthPads.length === 0 || monthPads.some((mp) => invM === mp);
  return yOk && mOk;
}

/** India FY: OR over (fyStart × quarter) pairs; empty quarters = full FY per start year. */
export function invoiceInFYSelection(
  inv: InvoiceRecord,
  fyStartYears: string[],
  fyQuarters: string[],
): boolean {
  if (!fyStartYears.length) return true;
  const qs = fyQuarters
    .map((q) => String(q).trim().toUpperCase())
    .filter((q) => q === 'Q1' || q === 'Q2' || q === 'Q3' || q === 'Q4');
  if (!qs.length) {
    return invoiceInAnyFYPeriodForFilter(inv, fyStartYears, null);
  }
  for (let i = 0; i < fyStartYears.length; i++) {
    const fy = String(fyStartYears[i]);
    for (let j = 0; j < qs.length; j++) {
      if (invoiceInFYPeriodForFilter(inv, fy, qs[j])) return true;
    }
  }
  return false;
}

export function filterClientInvoiceRecords(
  records: InvoiceRecord[],
  filters: InvoiceFilters
): InvoiceRecord[] {
  if (!Array.isArray(records)) return [];
  const {
    statusFilter = [],
    entityFilter = [],
    invoiceSearch = '',
    calendarYears = [],
    calendarMonths = [],
    fyYears = [],
    fyQuarters = [],
  } = filters;
  let filtered = records;
  if (statusFilter.length > 0) {
    const selected = new Set(
      statusFilter.map((s) => String(s).trim().toLowerCase()),
    );
    filtered = filtered.filter((record) =>
      selected.has(String(record.status ?? '').trim().toLowerCase()),
    );
  }
  if (entityFilter.length > 0) {
    filtered = filtered.filter((record) => entityFilter.includes(invoiceEntityKey(record)));
  }
  if (invoiceSearch.trim()) {
    const searchTerm = invoiceSearch.trim().toLowerCase();
    const searchTermNumeric = searchTerm.replace(/[^0-9]/g, '');
    filtered = filtered.filter((record) => {
      const invoiceNumber = record.invoice_number || `INV-${record.invoice_id || ''}`;
      const invoiceNumberLower = (invoiceNumber || '').toString().toLowerCase();
      const invoiceNumberNumeric = invoiceNumberLower.replace(/[^0-9]/g, '');
      return (
        invoiceNumberLower.includes(searchTerm) ||
        (!!searchTermNumeric && invoiceNumberNumeric.includes(searchTermNumeric))
      );
    });
  }
  if (calendarYears.length > 0 || calendarMonths.length > 0) {
    filtered = filtered.filter((r) =>
      invoiceInAnyCalendarSelection(r, calendarYears, calendarMonths),
    );
  }
  if (fyYears.length > 0) {
    filtered = filtered.filter((r) =>
      invoiceInFYSelection(r, fyYears, fyQuarters),
    );
  }
  return filtered;
}

/* ─── Default currency view ──────────────────────────────────────────────── */

export function defaultClientInvoiceCurrencyView(data: unknown): CurrencyView {
  const d = data as Record<string, unknown> | null | undefined;
  const bc =
    (d?.metadata as Record<string, unknown>)?.original_currency ||
    (d?.currency_analysis as Record<string, unknown>)?.primary_currency ||
    'USD';
  return String(bc).trim().toUpperCase() === 'USD' ? 'USD' : 'BASE';
}

/* ─── Entity key helpers ─────────────────────────────────────────────────── */

export function invoiceEntityKey(record: InvoiceRecord): string {
  const n = record?.entity_name;
  return n != null && String(n).trim() !== '' ? String(n).trim() : '__no_entity__';
}

/** Distinct `invoiceEntityKey` values for invoices matched to this project (same rule as totals). */
export function entityKeysForProject(
  project: ProjectAssociation,
  records: InvoiceRecord[],
): string[] {
  const keys = new Set<string>();
  if (!Array.isArray(records)) return [];
  for (const inv of records) {
    if (projectMatchesInvoiceEntity(project, inv.entity_name)) {
      keys.add(invoiceEntityKey(inv));
    }
  }
  return Array.from(keys).sort();
}

export function entityKeyLabel(key: string): string {
  return key === '__no_entity__' ? 'No entity' : key;
}

/* ─── Date formatting ────────────────────────────────────────────────────── */

export function formatInvoiceDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/* ─── CSV helpers ────────────────────────────────────────────────────────── */

export function escapeCsvCell(value: unknown): string {
  if (value === undefined || value === null) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function numForCsv(n: unknown): string {
  const x = Number(n);
  if (!Number.isFinite(x)) return '';
  return String(Math.round(x * 100) / 100);
}

/* ─── Deposit CSV export ─────────────────────────────────────────────────── */

function buildDepositExportTables(params: BuildDepositCsvParams): {
  meta: string[][];
  summary: string[][];
  paidInvoices: string[][];
  lineItems: string[][];
  unpaid: string[][];
  adjustments: string[][];
} {
  const {
    clientName,
    contactId,
    baseCurrency,
    metrics,
    paidDepositInvoices,
    unpaidDepositSubjectInvoices,
    adjustmentRows,
  } = params;
  const cur = (baseCurrency || 'USD').trim() || 'USD';
  const meta: string[][] = [
    ['Deposit information export'],
    ['Client name', clientName || ''],
    ['Contact ID', contactId != null && contactId !== '' ? String(contactId) : ''],
    ['Base currency', cur],
    ['Exported at (UTC)', new Date().toISOString()],
  ];
  const summary: string[][] = [
    ['Metric', `Amount_${cur}`, 'Amount_USD'],
    ['Total_deposit', numForCsv(metrics.depositBase), numForCsv(metrics.depositUsd)],
    ['Deposit_adjustment', numForCsv(metrics.depositAdjustmentBase), numForCsv(metrics.depositAdjustmentUsd)],
    ['Deposit_balance', numForCsv(metrics.netDepositBase), numForCsv(metrics.netDepositUsd)],
  ];
  const paidInvoices: string[][] = [
    [
      'invoice_id', 'invoice_number', 'subject', 'status', 'invoice_currency',
      `amount_${cur}`, 'amount_USD', 'invoice_date', 'due_date', 'paid_date',
    ],
    ...paidDepositInvoices.map((inv) => [
      String(inv.invoice_id ?? ''),
      inv.invoice_number ?? '',
      inv.subject ?? '',
      inv.status ?? '',
      inv.currency ?? '',
      numForCsv(depositInvoiceAmountBase(inv)),
      numForCsv(inv.amount_usd),
      inv.invoice_date ?? '',
      inv.due_date ?? '',
      inv.paid_date ?? '',
    ]),
  ];
  const lineItems: string[][] = [
    [
      'parent_invoice_id', 'parent_invoice_number', 'line_index',
      'description', 'quantity', 'unit_price', 'tax_percent',
      `line_total_${cur}`, 'line_total_USD', 'is_deposit_adjustment_pattern_line',
    ],
  ];
  paidDepositInvoices.forEach((inv) => {
    const pid = String(inv.invoice_id ?? '');
    const pnum = inv.invoice_number ?? '';
    (inv.line_items || []).forEach((line, li) => {
      lineItems.push([
        pid, pnum, String(li),
        line.description ?? '',
        numForCsv(line.quantity),
        numForCsv(line.unit_price ?? line.price),
        numForCsv(line.tax_percentage),
        numForCsv(line.line_total),
        numForCsv(line.line_total_usd),
        isDepositAdjustmentLineRecord(line) ? 'yes' : 'no',
      ]);
    });
  });
  const unpaid: string[][] = [
    ['invoice_id', 'invoice_number', 'subject', 'status', `amount_${cur}`, 'amount_USD', 'invoice_date'],
    ...unpaidDepositSubjectInvoices.map((inv) => [
      String(inv.invoice_id ?? ''),
      inv.invoice_number ?? '',
      inv.subject ?? '',
      inv.status ?? '',
      numForCsv(depositInvoiceAmountBase(inv)),
      numForCsv(inv.amount_usd),
      inv.invoice_date ?? '',
    ]),
  ];
  const adjustments: string[][] = [
    [
      'parent_invoice_id', 'parent_invoice_number', 'parent_subject', 'parent_status',
      'line_description', `line_total_${cur}`, 'line_total_USD',
      `adjustment_amount_${cur}`, 'adjustment_amount_USD',
    ],
    ...adjustmentRows.map(({ invoice: inv, line }) => [
      String(inv.invoice_id ?? ''),
      inv.invoice_number ?? '',
      inv.subject ?? '',
      inv.status ?? '',
      line.description ?? '',
      numForCsv(line.line_total),
      numForCsv(line.line_total_usd),
      numForCsv(depositAdjustmentLineAmountBase(line)),
      numForCsv(depositAdjustmentLineAmountUsd(line)),
    ]),
  ];
  return { meta, summary, paidInvoices, lineItems, unpaid, adjustments };
}

/** UTF-8 CSV with BOM — opens cleanly in Excel. */
export function buildDepositInfoCsv(params: BuildDepositCsvParams): string {
  const t = buildDepositExportTables(params);
  const lines: string[] = [];
  const pushRow = (cells: string[]) => lines.push(cells.map(escapeCsvCell).join(','));
  t.meta.forEach((r) => pushRow(r));
  pushRow([]);
  pushRow(['Summary']);
  t.summary.forEach((r) => pushRow(r));
  pushRow([]);
  pushRow(['Paid_deposit_invoices']);
  t.paidInvoices.forEach((r) => pushRow(r));
  pushRow([]);
  pushRow(['Paid_deposit_invoice_line_items']);
  t.lineItems.forEach((r) => pushRow(r));
  pushRow([]);
  pushRow(['Unpaid_deposit_subject_invoices']);
  t.unpaid.forEach((r) => pushRow(r));
  pushRow([]);
  pushRow(['Deposit_adjustment_lines']);
  t.adjustments.forEach((r) => pushRow(r));
  return `\uFEFF${lines.join('\r\n')}`;
}

export function downloadTextFile(filename: string, body: string, mime: string): void {
  const blob = new Blob([body], { type: mime || 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ─── Trend line amount ──────────────────────────────────────────────────── */

/** Per-invoice line amount for trend aggregation: USD or native BASE. */
export function invoiceTrendLineAmount(invoice: InvoiceRecord, currencyMode: CurrencyView): number {
  if (currencyMode === 'USD') {
    return parseFloat(String(invoice.amount_usd)) || 0;
  }
  if (invoice.amount != null && invoice.amount !== '') {
    const b = parseFloat(String(invoice.amount));
    if (!Number.isNaN(b)) return b;
  }
  return parseFloat(String(invoice.amount_usd)) || 0;
}
